import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Check, X, Crown, Lightning, Eye } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getH = () => ({});

const TIERS = [
    {
        id: 'free',
        name: 'Free',
        price: 0,
        label: 'Browse Only',
        color: 'text-muted-foreground',
        border: 'border-white/10',
        bg: 'bg-white/5',
        icon: Eye,
        features: [
            { text: 'Browse marketplace', included: true },
            { text: 'Access basic app features', included: true },
            { text: 'View leaderboards & social', included: true },
            { text: 'Buy items', included: false },
            { text: 'Sell or trade', included: false },
            { text: 'Open a storefront', included: false },
            { text: 'Governance voting', included: false },
        ],
    },
    {
        id: 'standard',
        name: 'Standard',
        price: 9.99,
        label: 'Most Popular',
        color: 'text-primary',
        border: 'border-primary/30',
        bg: 'bg-primary/5',
        icon: Lightning,
        features: [
            { text: 'Browse marketplace', included: true },
            { text: 'Access basic app features', included: true },
            { text: 'View leaderboards & social', included: true },
            { text: 'Buy items from marketplace', included: true },
            { text: 'Early access to new features', included: true },
            { text: 'Sell or trade', included: false },
            { text: 'Open a storefront', included: false },
            { text: 'Governance voting', included: false },
        ],
    },
    {
        id: 'vip',
        name: 'VIP',
        price: 29.99,
        label: 'Full Access',
        color: 'text-boltz-gold',
        border: 'border-boltz-gold/30',
        bg: 'bg-boltz-gold/5',
        icon: Crown,
        features: [
            { text: 'Browse marketplace', included: true },
            { text: 'Access basic app features', included: true },
            { text: 'View leaderboards & social', included: true },
            { text: 'Buy items from marketplace', included: true },
            { text: 'Early + priority access to features', included: true },
            { text: 'Sell & trade P2P', included: true },
            { text: 'Open personal storefront', included: true },
            { text: 'Governance voting rights', included: true },
        ],
    },
];

export default function Subscription() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [currentTier, setCurrentTier] = useState('free');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetch(`${API}/subscription/me`, { credentials: 'include', headers: getH() })
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d?.tier) setCurrentTier(d.tier); })
            .catch(e => console.warn('Sub fetch:', e.message));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const subscribe = async (tierId) => {
        if (tierId === currentTier) return;
        setLoading(true);
        try {
            // Free tier — just update directly
            if (tierId === 'free') {
                const res = await fetch(`${API}/subscription/update`, {
                    method: 'POST', credentials: 'include', headers: { ...getH(), 'Content-Type': 'application/json' },
                    body: JSON.stringify({ tier: tierId }),
                });
                if (res.ok) { setCurrentTier(tierId); toast.success('Switched to Free plan'); }
                else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
            } else {
                // Paid tiers — redirect to Stripe checkout
                const lookupKey = tierId === 'vip' ? 'vip_monthly' : 'standard_monthly';
                const res = await fetch(`${API}/payments/checkout`, {
                    method: 'POST', headers: { ...getH(), 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ lookup_key: lookupKey, origin_url: window.location.origin }),
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data.checkout_url) window.location.href = data.checkout_url;
                } else {
                    const err = await res.json();
                    toast.error(err.detail || 'Payment setup failed');
                }
            }
        } catch (e) {
            toast.error('Network error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout>
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
                    <span className="label-text text-boltz-gold text-glow-gold">MEMBERSHIP</span>
                    <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display" data-testid="subscription-title">Choose Your Plan</h1>
                    <p className="text-muted-foreground text-sm mt-2 max-w-md mx-auto">
                        Unlock marketplace features, P2P trading, storefronts, and governance
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="tier-grid">
                    {TIERS.map((tier, idx) => {
                        const Icon = tier.icon;
                        const isCurrent = currentTier === tier.id;
                        const isUpgrade = TIERS.findIndex(t => t.id === currentTier) < idx;

                        return (
                            <motion.div key={tier.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.1 }}>
                                <Card className={`relative overflow-hidden h-full ${tier.bg} border ${isCurrent ? tier.border + ' ring-1 ring-primary/20' : 'border-white/10'}`}
                                    data-testid={`tier-${tier.id}`}>
                                    {tier.id === 'standard' && (
                                        <div className="absolute top-0 left-0 right-0 bg-primary text-black text-[10px] font-display text-center py-0.5 tracking-wider">
                                            MOST POPULAR
                                        </div>
                                    )}
                                    <CardContent className={`p-5 flex flex-col h-full ${tier.id === 'standard' ? 'pt-7' : ''}`}>
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className={`w-9 h-9 rounded-lg ${tier.bg} border ${tier.border} flex items-center justify-center`}>
                                                <Icon size={18} className={tier.color} weight="fill" />
                                            </div>
                                            <div>
                                                <h3 className={`font-display font-bold ${tier.color}`}>{tier.name}</h3>
                                                <p className="text-[10px] text-muted-foreground">{tier.label}</p>
                                            </div>
                                        </div>

                                        <div className="mb-4">
                                            {tier.price === 0 ? (
                                                <span className="text-2xl font-black font-mono text-foreground">Free</span>
                                            ) : (
                                                <div className="flex items-baseline gap-1">
                                                    <span className="text-2xl font-black font-mono text-foreground">${tier.price}</span>
                                                    <span className="text-xs text-muted-foreground">/month</span>
                                                </div>
                                            )}
                                        </div>

                                        <ul className="space-y-2 flex-1 mb-4">
                                            {tier.features.map(f => (
                                                <li key={f.text} className="flex items-start gap-2 text-xs">
                                                    {f.included ? (
                                                        <Check size={14} className={`${tier.color} shrink-0 mt-0.5`} weight="bold" />
                                                    ) : (
                                                        <X size={14} className="text-muted-foreground/40 shrink-0 mt-0.5" />
                                                    )}
                                                    <span className={f.included ? 'text-foreground' : 'text-muted-foreground/40'}>{f.text}</span>
                                                </li>
                                            ))}
                                        </ul>

                                        {isCurrent ? (
                                            <Badge className={`w-full justify-center py-1.5 text-xs ${tier.bg} ${tier.color} border ${tier.border}`}
                                                data-testid={`current-${tier.id}`}>
                                                Current Plan
                                            </Badge>
                                        ) : (
                                            <Button onClick={() => subscribe(tier.id)} disabled={loading}
                                                className={`w-full text-xs font-display ${
                                                    tier.id === 'vip' ? 'bg-boltz-gold text-black hover:bg-boltz-gold/80' :
                                                    tier.id === 'standard' ? 'bg-primary text-black hover:bg-primary/80' :
                                                    'bg-white/10 text-foreground hover:bg-white/20'
                                                }`} data-testid={`select-${tier.id}`}>
                                                {isUpgrade ? 'Upgrade' : 'Select'}
                                            </Button>
                                        )}
                                    </CardContent>
                                </Card>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </Layout>
    );
}
